/**
 * 71.4 step 1 (ARCHITECTURE §5.42 "Damage at two scales"; DECISIONS 344, ruled 356): the hand-on — the fine edits' (the
 * digs', the craterlets') facet-scale share of the ground, carried by the mesh for the readers that read the mesh alone.
 *
 * - **The two bands.** A dig or a craterlet stays an analytic record the fine ground adds whole (core/fineTerrain.ts); its
 *   projection onto the mesh's hat functions is a per-vertex lift, `coarseLift`, added only for the coarse readers: the
 *   kernel (its flights, its crater lookups) and the action clock's shells (core/actionShells.ts stepShells). The fine
 *   readers, 63.5's strata, 71.2b's relief shares and the shaping dents read `heightmap` and never see it, so they are bit
 *   for bit as without it.
 * - **The projection (lumped).** The fine edits compose as a sum, so the lift is a sum per record. A record is small beside
 *   a facet (a craterlet 1–3 people across, a facet 12–50), so a sampled pattern would alias it; its hat-weighted integral
 *   is instead its volume at its centre — exact for a shape symmetric about its centre inside one facet, where a corner's
 *   weight is linear. Each welded vertex `v` lifts by Σ_r w_v(c_r)·V_r / A_v: `w_v` the vertex's barycentric weight at the
 *   record's centre on the base mesh, `V_r` the record's volume, `A_v` a third of the facets' area round it (the lumped
 *   mass). The weights sum to 1 at any point, so Σ_v A_v·lift_v is Σ_r V_r: the mesh carries the edits' volume exactly,
 *   spread over the facets they fell in.
 * - **Deterministic.** Each record's three weights and its volume depend on the base mesh and the record alone (never the
 *   heights), kept per record object (a deepened craterlet, a dug-on dig is a new one). The lift is the sum in the lists'
 *   order — digs, then craterlets — so a replayed planet gets the same bits as the live one.
 *
 * Off (`GameConfig.coarseHandOn` false) or with no fine edits, the coarse readers read `heightmap` as before.
 */
import type { Craterlet, Dig, PlanetState } from '../types';
import { craterletShape } from './craterlets';
import { digGeometry, DIG_REACH, type DigKind } from './dig';
import { meshWeld } from './materialRelief';
import { PERSON_BODY_HEIGHT } from './scaleProfile';
import { getTerrainIndex, lastSurfaceTriangle, lastSurfaceU, lastSurfaceV, look, nearestSurfaceVertex, surfaceRadiusLook } from './surface';
import type { GroundMaterial } from '../types/ground';

/** A record's share of the lift: the three welded vertices of the facet its centre is in, each one's weight × the volume (world³). */
interface Share { v0: number; v1: number; v2: number; m0: number; m1: number; m2: number }

/** The base mesh (no heights) per basePositions: a zero heightmap to find a direction's facet on, and each welded vertex's lumped area. */
interface Base { zeros: Float32Array; area: Float64Array }
const bases = new WeakMap<Float32Array, Base>();
function baseOf(bp: Float32Array): Base {
  let b = bases.get(bp);
  if (b) return b;
  const w = meshWeld(bp), entries = Math.floor(bp.length / 3), V = w.facetsAt.length - 1;
  const area = new Float64Array(V);
  for (let t = 0; t * 3 + 2 < entries; t++) {
    const i0 = t * 9;
    const ax = bp[i0 + 3] - bp[i0], ay = bp[i0 + 4] - bp[i0 + 1], az = bp[i0 + 5] - bp[i0 + 2];
    const bx = bp[i0 + 6] - bp[i0], by = bp[i0 + 7] - bp[i0 + 1], bz = bp[i0 + 8] - bp[i0 + 2];
    const cx = ay * bz - az * by, cy = az * bx - ax * bz, cz = ax * by - ay * bx;
    const third = Math.sqrt(cx * cx + cy * cy + cz * cz) / 6;
    for (let k = 0; k < 3; k++) area[w.vertexOf[t * 3 + k]] += third;
  }
  b = { zeros: new Float32Array(entries), area };
  bases.set(bp, b);
  return b;
}

/**
 * A craterlet's volume (world³, − below the ground): its bowl, −depth·πb²/2, and its rim, a 4u(1−u) profile from b out to
 * rimOut (∫ over the ring: (4/3)π·rim·L·b + (2/3)π·rim·L², L = rimOut − b), in person heights cubed × the person's.
 */
export function craterletVolume(c: Pick<Craterlet, 'kind' | 'material' | 'scale'>, person: number): number {
  const s = craterletShape(c);
  if (!s) return 0;
  const b = s.radius, L = s.rimOut - s.radius;
  const people3 = -s.depth * Math.PI * b * b / 2 + (4 / 3) * Math.PI * s.rim * L * b + (2 / 3) * Math.PI * s.rim * L * L;
  return people3 * person * person * person;
}

/** A dig's volume in person units cubed (its hole and its spoil, summed over a 0.02-unit grid out to DIG_REACH), per shape. */
const digVolumes = new Map<string, number>();
function digVolumeUnits(kind: DigKind, material: GroundMaterial, done: number): number {
  const key = `${kind}|${material}|${done}`;
  let v = digVolumes.get(key);
  if (v !== undefined) return v;
  const g = digGeometry(kind, material, done);
  v = 0;
  if (g) {
    const step = 0.02, n = Math.ceil(DIG_REACH / step);
    for (let i = -n; i < n; i++) for (let j = -n; j < n; j++) v += g.height((i + 0.5) * step, (j + 0.5) * step);
    v *= step * step;
  }
  digVolumes.set(key, v);
  return v;
}
/** A dig's volume (world³): its shape's, in person units scaled to the person (1.8 units a person). */
export function digVolume(d: Pick<Dig, 'kind' | 'material' | 'done'>, k: number): number {
  const unit = (PERSON_BODY_HEIGHT * k) / 1.8;
  return digVolumeUnits(d.kind, d.material, d.done) * unit * unit * unit;
}

const shares = new WeakMap<object, { bp: Float32Array; k: number; s: Share | null }>();
/** A record's share: its centre's facet on the base mesh, the facet's corners welded, each weight × the record's volume. */
function shareOf(planet: PlanetState, rec: Dig | Craterlet, volumeOf: () => number, k: number): Share | null {
  const kept = shares.get(rec);
  if (kept && kept.bp === planet.basePositions && kept.k === k) return kept.s;
  let s: Share | null = null;
  const volume = volumeOf();
  if (volume !== 0) {
    const bp = planet.basePositions, w = meshWeld(bp), base = baseOf(bp);
    look[0] = rec.dir.x; look[1] = rec.dir.y; look[2] = rec.dir.z; look[3] = planet.radius;
    surfaceRadiusLook(getTerrainIndex(bp), base.zeros, bp);
    const t = lastSurfaceTriangle();
    if (t >= 0) {
      const u = lastSurfaceU(), v = lastSurfaceV();
      s = { v0: w.vertexOf[t * 3], v1: w.vertexOf[t * 3 + 1], v2: w.vertexOf[t * 3 + 2], m0: (1 - u - v) * volume, m1: u * volume, m2: v * volume };
    } else {
      // (no facet met: the nearest vertex takes it whole)
      const at = w.vertexOf[nearestSurfaceVertex(planet, rec.dir)];
      s = { v0: at, v1: at, v2: at, m0: volume, m1: 0, m2: 0 };
    }
  }
  shares.set(rec, { bp: planet.basePositions, k, s });
  return s;
}

/**
 * The lift per welded vertex (world units, Float64), or null with no fine edits. Summed in the lists' order: digs, then
 * craterlets. Kept per list pair.
 */
interface Lift { bp: Float32Array; digs: unknown; craterlets: unknown; k: number; lift: Float64Array | null; moved: Int32Array }
const lifts = new WeakMap<object, Lift>();
export function coarseLiftOf(planet: PlanetState, k: number): Float64Array | null {
  return liftOf(planet, k)?.lift ?? null;
}
function liftOf(planet: PlanetState, k: number): Lift | null {
  const digs = planet.digs, craterlets = planet.craterlets;
  if (!digs?.length && !craterlets?.length) return null;
  // (kept by the list that changes most — planets can share a mesh's basePositions)
  const key: object = craterlets?.length ? craterlets : digs!;
  const kept = lifts.get(key);
  if (kept && kept.bp === planet.basePositions && kept.digs === digs && kept.craterlets === craterlets && kept.k === k) return kept;
  const base = baseOf(planet.basePositions), mass = new Float64Array(base.area.length), seen = new Uint8Array(base.area.length), touched: number[] = [];
  // (the vertices touched, in the order first met: only they are divided and handed on — the rest of the mesh is never walked)
  const at = (v: number, m: number) => { if (!seen[v]) { seen[v] = 1; touched.push(v); } mass[v] += m; };
  const add = (s: Share | null) => { if (s) { at(s.v0, s.m0); at(s.v1, s.m1); at(s.v2, s.m2); } };
  const person = PERSON_BODY_HEIGHT * k;
  for (const d of digs ?? []) add(shareOf(planet, d, () => digVolume(d, k), k));
  for (const c of craterlets ?? []) add(shareOf(planet, c, () => craterletVolume(c, person), k));
  const moved: number[] = [];
  for (const v of touched) if (mass[v] !== 0) { mass[v] /= base.area[v]; moved.push(v); }
  const out: Lift = { bp: planet.basePositions, digs, craterlets, k, lift: moved.length ? mass : null, moved: Int32Array.from(moved) };
  lifts.set(key, out);
  return out;
}

/** The volume the lift adds to the mesh (world³): Σ over welded vertices of its lumped area × its lift (what the tests weigh against the edits'). */
export function liftedVolume(planet: PlanetState, k: number): number {
  const lift = coarseLiftOf(planet, k);
  if (!lift) return 0;
  const area = baseOf(planet.basePositions).area;
  let v = 0;
  for (let i = 0; i < lift.length; i++) v += lift[i] * area[i];
  return v;
}

/**
 * The heights the coarse readers read: `heightmap` with the lift on every corner (Float32, as the mesh), or `heightmap`
 * itself when the hand-on is off or nothing is dug. A new array when the heightmap or the fine edits change; the same one
 * otherwise (the kernel's sync and the surface tables key on it).
 */
const lifted = new WeakMap<Float32Array, { lift: Float64Array; heights: Float32Array }>();
const liftedArrays = new WeakSet<Float32Array>();
export function coarseHeights(planet: PlanetState, k: number, on: boolean): Float32Array {
  if (!on || planet.type !== 'planet') return planet.heightmap;
  const l = liftOf(planet, k), lift = l?.lift;
  if (!l || !lift) return planet.heightmap;
  const kept = lifted.get(planet.heightmap);
  if (kept && kept.lift === lift) return kept.heights;
  // (the mesh's own bits everywhere, then the moved vertices' corners: each facet round a vertex holds it at one corner)
  const hm = planet.heightmap, w = meshWeld(planet.basePositions), heights = hm.slice();
  for (const v of l.moved) {
    for (let q = w.facetsAt[v]; q < w.facetsAt[v + 1]; q++) {
      const f = w.facets[q] * 3;
      for (let i = f; i < f + 3; i++) if (w.vertexOf[i] === v) heights[i] = hm[i] + lift[v];
    }
  }
  lifted.set(hm, { lift, heights });
  liftedArrays.add(heights);
  return heights;
}
/** Whether `heights` is a lifted array coarseHeights made (the kernel's sync: one it holds must be replaced when it is stale). */
export function isLiftedHeights(heights: Float32Array | undefined): boolean {
  return !!heights && liftedArrays.has(heights);
}

/**
 * The planets as the coarse readers see them: each with a lift given `coarseHeights` as its heightmap (a copy of the planet,
 * kept while its inputs are the same), the list itself when none has one.
 */
const coarseLists = new WeakMap<readonly PlanetState[], { k: number; on: boolean; out: PlanetState[] }>();
const coarseOne = new WeakMap<PlanetState, { heights: Float32Array; out: PlanetState }>();
export function coarsePlanets(planets: PlanetState[], k: number, on: boolean): PlanetState[] {
  if (!on) return planets;
  const kept = coarseLists.get(planets);
  if (kept && kept.k === k && kept.on === on) return kept.out;
  let out = planets;
  for (let i = 0; i < planets.length; i++) {
    const p = planets[i], heights = coarseHeights(p, k, on);
    if (heights === p.heightmap) continue;
    if (out === planets) out = planets.slice();
    const one = coarseOne.get(p);
    if (one && one.heights === heights) { out[i] = one.out; continue; }
    out[i] = { ...p, heightmap: heights };
    coarseOne.set(p, { heights, out: out[i] });
  }
  coarseLists.set(planets, { k, on, out });
  return out;
}
